import React, { useMemo, useRef, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import useUnmountedRef from "@/hooks/useUnmountedRef";
import { useLockScroll } from "@/hooks/useLockScroll";
import {
  renderToContainer,
  PropagationEvent,
  withStopPropagation,
  mergeProps,
  ShouldRender,
  NativeProps,
  withNativeProps,
} from "@/utils/components-utils";
import type { GetContainer } from "@/utils/components-utils";

import styles from "./mask.module.scss";

const opacityRecord = {
  default: 0.55,
  thin: 0.35,
  thick: 0.75,
};

export type MaskProps = {
  visible?: boolean;
  onMaskClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  destroyOnClose?: boolean;
  forceRender?: boolean;
  disableBodyScroll?: boolean;
  color?: "black" | "white";
  opacity?: "default" | "thin" | "thick" | number;
  getContainer?: GetContainer;
  afterShow?: () => void;
  afterClose?: () => void;
  stopPropagation?: PropagationEvent[];
  children?: React.ReactNode;
} & NativeProps<"--z-index">;

const defaultProps = {
  visible: true,
  destroyOnClose: false,
  forceRender: false,
  color: "black",
  opacity: "default",
  disableBodyScroll: true,
  getContainer: null,
  stopPropagation: ["click"],
};

export const Mask: React.FC<MaskProps> = (p) => {
  const props = mergeProps(defaultProps, p);

  const ref = useRef<HTMLDivElement>(null);

  useLockScroll(ref, props.visible && props.disableBodyScroll);

  const background = useMemo(() => {
    const opacity = opacityRecord[props.opacity] ?? props.opacity;
    const rgb = props.color === "white" ? "255, 255, 255" : "0, 0, 0";
    return `rgba(${rgb}, ${opacity})`;
  }, [props.color, props.opacity]);

  const [active, setActive] = useState(props.visible);

  const unmountedRef = useUnmountedRef();
  const { opacity } = useSpring({
    opacity: props.visible ? 1 : 0,
    config: {
      precision: 0.01,
      mass: 1,
      tension: 250,
      friction: 30,
      clamp: true,
    },
    onStart: () => {
      setActive(true);
    },
    onRest: () => {
      if (unmountedRef.current) return;
      setActive(props.visible);
      if (props.visible) {
        props.afterShow?.();
      } else {
        props.afterClose?.();
      }
    },
  });

  const node = withStopPropagation(
    props.stopPropagation,
    withNativeProps(
      props,
      <animated.div
        className={styles.rfMask}
        ref={ref}
        style={{
          ...props.style,
          background,
          opacity,
          display: active ? undefined : "none",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            props.onMaskClick?.(e);
          }
        }}
      >
        {props.onMaskClick && (
          <div
            className={styles.ariaButton}
            role="button"
            onClick={props.onMaskClick}
          />
        )}
        <div className={styles.content}>{props.children}</div>
      </animated.div>
    )
  );

  return (
    <ShouldRender
      active={active}
      forceRender={props.forceRender}
      destroyOnClose={props.destroyOnClose}
    >
      {renderToContainer(props.getContainer, node)}
    </ShouldRender>
  );
};
